import { useQuery } from "@tanstack/react-query";

import type { ServiceStatus } from "@/client-types/service-status";
import { parseServiceStatus } from "@/utility/serviceStatus";

/**
 * A separate public repo, so the message can be edited from a phone during an outage without a
 * build, an upload, or the backend that is down. Public because raw.githubusercontent requires a
 * token for private repos, and a token shipped in the client bundle would be public anyway.
 */
const STATUS_URL = "https://raw.githubusercontent.com/MRThomsen1/cykeljusteringen-status/main/status.json";

const REFETCH_INTERVAL_MS = 60_000;
const TIMEOUT_MS = 5_000;

async function fetchServiceStatus(): Promise<ServiceStatus | null> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    // GitHub serves raw files with max-age=300. Changing the URL once a minute sidesteps that cache
    // without hammering it, so a message posted from a phone shows up within a minute.
    const cacheBuster = Math.floor(Date.now() / REFETCH_INTERVAL_MS);

    const response = await fetch(`${STATUS_URL}?t=${cacheBuster}`, { signal: controller.signal, cache: "no-store" });
    if (!response.ok) return null;

    return parseServiceStatus(await response.json());
  } catch {
    // A missing file, a timeout, malformed JSON, GitHub being down — none of it is worth surfacing.
    // No status simply means no banner.
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

export default function useServiceStatus(): ServiceStatus | null {
  const { data } = useQuery({
    queryKey: ["service-status"],
    queryFn: fetchServiceStatus,
    refetchInterval: REFETCH_INTERVAL_MS,
    staleTime: 30_000,
    retry: 1,
  });

  return data ?? null;
}
